import { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Divider,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Unstable_Grid2 as Grid
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { useShopDetails } from '../../hooks/use-shop';
import { CREATE_LOCATION, UPDATE_LOCATION } from '../../gql/queries';
import { BusinessType } from '../../gql/graphql';

interface LocationFormProps {
	data?: {
		_id?: string;
		name?: string;
		address?: {
			city?: string;
			street?: string;
			state?: string;
			zipcode?: string;
		};
		email?: string;
		phone?: string;
		printerName?: string;
		businessType?: BusinessType | null;
	};
	onClose: () => void;
}

export default function EditLocation({ data, onClose }: LocationFormProps) {
	const { shopId } = useShopDetails();
	const [formData, setFormData] = useState({
		name: data?.name || '',
		city: data?.address?.city || '',
		street: data?.address?.street || '',
		state: data?.address?.state || '',
		zipcode: data?.address?.zipcode || '',
		email: data?.email || '',
		phone: data?.phone || '',
		printerName: data?.printerName || '',
		// Locations stored before this field existed have none; they are salons.
		businessType: data?.businessType || BusinessType.Salon
	});

	const [createLocation, { loading: creating }] = useMutation(CREATE_LOCATION);
	const [updateLocation, { loading: updating }] = useMutation(UPDATE_LOCATION);

	const isEdit = !!data?._id;
	const loading = creating || updating;

	useEffect(() => {
		if (data) {
			setFormData({
				name: data.name || '',
				city: data.address?.city || '',
				street: data.address?.street || '',
				state: data.address?.state || '',
				zipcode: data.address?.zipcode || '',
				email: data.email || '',
				phone: data.phone || '',
				printerName: data.printerName || '',
				businessType: data.businessType || BusinessType.Salon
			});
		}
	}, [data]);

	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
	}, []);

	const handleSubmit = useCallback(async (event: React.FormEvent) => {
		event.preventDefault();

		// Validation
		if (!formData.name.trim()) {
			toast.error('Location name is required');
			return;
		}
		if (!formData.city.trim()) {
			toast.error('City is required');
			return;
		}
		if (!formData.email.trim()) {
			toast.error('Email is required');
			return;
		}
		if (!formData.phone.trim()) {
			toast.error('Phone is required');
			return;
		}

		const locationInput = {
			name: formData.name.trim(),
			address: {
				city: formData.city.trim(),
				street: formData.street.trim(),
				state: formData.state.trim(),
				zipcode: formData.zipcode.trim(),
				landmark: '' // Add default value
			},
			email: formData.email.trim(),
			phone: formData.phone.trim(),
			printerName: formData.printerName?.trim() || '',
			businessType: formData.businessType
		};

		try {
			if (isEdit) {
				// Update existing location - FIX: Pass as ShopInput with location array
				await updateLocation({
					variables: {
						shopId: shopId,
						locationId: data._id,
						location: {
							name: '', // Not used in update, but required by ShopInput type
							location: [locationInput]
						}
					}
				});
				toast.success('Location updated successfully');
			} else {
				// Create new location
				await createLocation({
					variables: {
						shop: {
							shopId: shopId,
							location: [locationInput]
						}
					}
				});
				toast.success('Location created successfully');
			}
			onClose();
		} catch (error: any) {
			toast.error(`Failed to ${isEdit ? 'update' : 'create'} location: ${error.message}`);
		}
	}, [formData, isEdit, data, shopId, createLocation, updateLocation, onClose]);

	return (
		<form onSubmit={handleSubmit}>
			<Card>
				<CardHeader
					title={isEdit ? 'Edit Location' : 'Create New Location'}
					subheader={isEdit ? 'Update location details' : 'Add a new location to your shop'}
				/>
				<Divider />
				<CardContent>
					<Grid container spacing={3}>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Location Name"
								name="name"
								onChange={handleChange}
								required
								value={formData.name}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="City"
								name="city"
								onChange={handleChange}
								required
								value={formData.city}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<FormControl fullWidth disabled={loading}>
								<InputLabel>Business type</InputLabel>
								<Select
									label="Business type"
									name="businessType"
									value={formData.businessType}
									onChange={(event) =>
										setFormData((prev) => ({
											...prev,
											businessType: event.target.value as BusinessType
										}))
									}
								>
									<MenuItem value={BusinessType.Salon}>Salon</MenuItem>
									<MenuItem value={BusinessType.FoodDelivery}>Restaurant</MenuItem>
								</Select>
								<FormHelperText>
									What this location sells. A restaurant has no services, so the Services
									section is hidden while this is set to Restaurant. Existing services are kept.
								</FormHelperText>
							</FormControl>
						</Grid>
						<Grid xs={12}>
							<TextField
								fullWidth
								label="Street Address"
								name="street"
								onChange={handleChange}
								value={formData.street}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="State"
								name="state"
								onChange={handleChange}
								value={formData.state}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Zip Code"
								name="zipcode"
								onChange={handleChange}
								value={formData.zipcode}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Email"
								name="email"
								type="email"
								onChange={handleChange}
								required
								value={formData.email}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12} md={6}>
							<TextField
								fullWidth
								label="Phone"
								name="phone"
								onChange={handleChange}
								required
								value={formData.phone}
								disabled={loading}
							/>
						</Grid>
						<Grid xs={12}>
							<TextField
								fullWidth
								label="Printer Name (Optional)"
								name="printerName"
								onChange={handleChange}
								value={formData.printerName}
								helperText="Name of the printer configured for this location"
								disabled={loading}
							/>
						</Grid>
					</Grid>
				</CardContent>
				<Divider />
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 2,
						p: 2
					}}
				>
					<Button
						color="inherit"
						onClick={onClose}
						disabled={loading}
					>
						Cancel
					</Button>
					<Button
						color="primary"
						type="submit"
						variant="contained"
						disabled={loading}
					>
						{loading ? 'Saving...' : isEdit ? 'Update Location' : 'Create Location'}
					</Button>
				</Box>
			</Card>
		</form>
	);
}
